import { useEffect, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import { TELEGRAM_URL } from '../lib/contact';

/**
 * What this platform is, the terms it is used on, and what it does with
 * people's information.
 *
 * Reachable without signing in, because the people most likely to need it are
 * a candidate deciding what a score here means, a parent asking what is held
 * about their child, and a centre owner deciding whether to use it. Linked from
 * the menu and the sign-in form rather than buried in a footer for the same
 * reason.
 *
 * The terms rest on Target being free and for practice only: that is what lets
 * them leave the outcome of a mock, and what a centre does with it, with the
 * centre. If Target ever charges, the "Free" and liability panels must be
 * rewritten before the first payment — a consumer who pays has rights these
 * terms cannot take away.
 *
 * Every statement in the privacy notice is a statement about the code, and was
 * checked against it: what the tables hold, the one cookie, what session
 * storage keeps, the two automatic deletions in main.ts, and the absence of IP
 * addresses and browser details. Change the code and this page changes with it
 * — a notice that promises more than the platform does is worse than none.
 */

const UPDATED = '8 October 2026';

/** The one way to reach us: every "tell us" on this page points here. */
function Telegram() {
  return (
    <a href={TELEGRAM_URL} target="_blank" rel="noopener noreferrer">
      Telegram
    </a>
  );
}

function Points({ items }: { items: ReactNode[] }) {
  return (
    <ul className="legal__list">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="panel stack">
      <h3 className="panel__title">{title}</h3>
      {children}
    </section>
  );
}

function Terms({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="legal__terms">
      {rows.map(([term, detail]) => (
        <div key={term}>
          <dt>{term}</dt>
          <dd>{detail}</dd>
        </div>
      ))}
    </dl>
  );
}

export function LegalPage() {
  // The sign-in form links straight to the terms or the privacy notice; the
  // router does not scroll to a #fragment on its own.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);

  return (
    <div className="legal">
      <PageHeader
        eyebrow="About & legal"
        title="About Target, terms of use and privacy"
        meta={<p className="muted">Last updated {UPDATED}.</p>}
      />

      <nav className="legal__toc" aria-label="On this page">
        <a href="#about">About Target</a>
        <a href="#terms">Terms of use</a>
        <a href="#privacy">Privacy notice</a>
        <a href="#contact">Contact</a>
      </nav>

      <div className="stack legal__body">
        <h2 className="legal__part" id="about">
          About Target
        </h2>

        <Panel title="An independent practice platform">
          <p>
            Target is a platform for language centres to run practice exams in the
            computer-delivered format: the exam screen, a library of practice papers, whole-room
            test days and marking.
          </p>
          <p>
            It is not affiliated with, endorsed by, sponsored by, approved by or connected to the
            British Council, IDP Education, Cambridge University Press &amp; Assessment, ETS, Pearson,
            or any other examination provider, test owner or awarding body.
          </p>
        </Panel>

        <Panel title="Free, and not run for profit">
          <p>
            Target is offered to language centres and their candidates free of charge, and it is not
            run to make a profit. It exists for one reason: so that a candidate who has prepared on
            paper can rehearse the same exam on a computer — the screen, the clock and the question
            types — in a classroom, rather than meet them for the first time on the day they pay to
            sit the real thing.
          </p>
        </Panel>

        <Panel title="Scores are practice estimates, not results">
          <p>
            Nothing produced here is an official test result, an official band score, a certificate,
            or evidence of a qualification. Scores shown are practice estimates, produced for
            teaching by the centre a candidate is enrolled with. Only the organisation that owns a
            test can issue a result for that test.
          </p>
          <p>
            No certificate or downloadable result form is issued by this platform, and none should
            be presented to a university, an employer or an immigration authority.
          </p>
        </Panel>

        <Panel title="Trade marks belong to their owners">
          <p>
            IELTS is a registered trade mark of the British Council, IDP Education and Cambridge
            University Press &amp; Assessment. Other examination names are trade marks of their
            respective owners. Where Target or a centre using it names an examination, it does so
            only to describe what the practice material is modelled on — as a study guide names the
            exam it prepares a reader for. No association, approval or sponsorship is claimed or
            implied.
          </p>
        </Panel>

        <Panel title="Practice papers">
          <p>
            Target comes with a library of practice papers that centres can set, and each centre can
            add papers of its own. A centre that uploads material is responsible for having the
            right to use it.
          </p>
          <p>
            If you believe something on Target uses your work without permission, message us on{' '}
            <Telegram /> saying what it is and where it appears. We will look into it promptly and
            take it down where it infringes.
          </p>
        </Panel>

        <h2 className="legal__part" id="terms">
          Terms of use
        </h2>
        <p className="legal__lede">
          The terms everyone who uses Target accepts: language centres, their administrators and
          teachers, and their candidates. If you do not accept them, please do not use Target.
        </p>

        <Panel title="These terms">
          <p>
            By using Target you accept these terms. A centre that uses Target also has a written
            agreement with us, which sets out how we handle its candidates’ information; where that
            agreement and these terms differ, the agreement applies.
          </p>
        </Panel>

        <Panel title="Free, and for practice">
          <p>
            Target is offered free of charge. It is a tool for rehearsing the format of a
            computer-delivered exam. No score on Target predicts or guarantees a result in any
            official examination, and using it does not guarantee any standard or outcome.
          </p>
        </Panel>

        <Panel title="Who can use it">
          <p>
            A centre joins Target by arrangement with us. Its administrator creates the accounts for
            its teachers and candidates; nobody else can create one. A candidate under 18 uses
            Target through their centre, which must have written consent from their parent or
            guardian.
          </p>
        </Panel>

        <Panel title="Your account">
          <p>
            Keep your sign-in number and password to yourself. You are responsible for what is done
            with your account. If you think someone else has used it, tell your centre at once, or
            message us on <Telegram />.
          </p>
        </Panel>

        <Panel title="What a centre is responsible for">
          <Points
            items={[
              <>
                having a lawful basis for everything it enters, and the consent the law requires —
                including a parent’s or guardian’s written consent for every candidate under 18;
              </>,
              <>entering accurate information, and only what Target needs;</>,
              <>
                giving accounts only to its own staff and candidates, and closing the accounts of
                staff who leave;
              </>,
              <>having the right to use every paper, recording and image it uploads;</>,
              <>
                how it runs its mock exams, and anything it decides or tells a candidate on the
                strength of a practice score;
              </>,
              <>
                keeping its own copies of what it needs — results can be downloaded at any time, and
                Writing scripts are deleted two days after the result is released;
              </>,
              <>
                the computers, network and browsers its candidates sit on, including Safe Exam
                Browser where it uses it;
              </>,
              <>how its staff and candidates use Target.</>,
            ]}
          />
        </Panel>

        <Panel title="What nobody may do">
          <Points
            items={[
              <>break the law, or use Target to infringe anyone’s rights;</>,
              <>get into an account, a centre or information that is not theirs;</>,
              <>get round Target’s security, access controls, rate limits or exam controls;</>,
              <>
                copy, decompile or reverse engineer Target’s software, except where the law allows
                it;
              </>,
              <>upload malicious code, or anything unlawful, abusive or misleading;</>,
              <>
                overload or disrupt the service, or test its defences without our written
                permission;
              </>,
              <>copy, share, sell or publish papers, answer keys or candidates’ work outside the centre.</>,
            ]}
          />
        </Panel>

        <Panel title="Confidentiality for administrators and teachers">
          <p>
            What you see about candidates — their names, results and writing — is confidential. Use
            it only for your work at the centre, and do not copy, share or publish it, or the papers
            and answer keys, outside the centre. This continues after you leave the centre.
          </p>
        </Panel>

        <Panel title="Who owns what">
          <p>
            Target’s software, name and design belong to its operator. Papers in the library belong
            to their authors and may be used only on Target, for practice at the centre. What a
            centre uploads remains the centre’s; it permits us to store, process and display it only
            to run Target for the centre, for as long as the centre uses Target.
          </p>
        </Panel>

        <Panel title="Provided as it is">
          <p>
            Because Target is free, it is provided as it is and as available. We work to keep it
            running, secure and accurate, but we do not promise that it will always be available or
            free of errors, that it suits any particular purpose, or that a practice score matches
            an official one. To the extent the law allows, no other promise is made, whether stated
            or implied.
          </p>
        </Panel>

        <Panel title="Limits on our responsibility">
          <p>To the fullest extent the law allows, the operator of Target is not responsible for:</p>
          <Points
            items={[
              <>any result in an official examination, or any decision made on a practice score;</>,
              <>
                a paper that ends early or is lost because of a computer, browser, power or network
                problem, or because the candidate left the exam screen;
              </>,
              <>information or work that is lost, including Writing scripts once deleted;</>,
              <>the service being unavailable, slow or interrupted;</>,
              <>what a centre, its staff or its candidates do on Target, or with anything from it;</>,
              <>indirect or consequential loss, including lost income, opportunity or reputation.</>,
            ]}
          />
          <p>
            If the operator is nonetheless found responsible for a loss, its total liability is
            limited to the amount the person claiming paid for Target in the twelve months before the
            claim, unless the law requires otherwise.
          </p>
          <p>
            Nothing in these terms limits responsibility that the law does not allow to be limited —
            such as for intentional wrongdoing — or takes away any right a person has under consumer
            protection law.
          </p>
        </Panel>

        <Panel title="A centre’s responsibility to us">
          <p>
            If a claim is brought against the operator because a centre or its staff broke these
            terms or the law — for example by entering someone’s information without consent, or by
            uploading a paper it had no right to use — the centre will meet the cost of that claim,
            including reasonable legal costs, to the extent the law allows.
          </p>
        </Panel>

        <Panel title="Suspending and stopping">
          <p>
            We may suspend an account or a centre’s access, or remove material, if these terms or the
            law are broken, to protect candidates, other centres or the service, or where the law
            requires; where we can, we will say why first. A centre may stop using Target at any
            time.
          </p>
          <p>
            Because Target is free, we may change or remove features, or stop offering it. Before
            stopping, we will give centres at least 30 days’ notice, so that they can download their
            results.
          </p>
        </Panel>

        <Panel title="Other services">
          <p>
            Telegram, where we answer messages, and Safe Exam Browser, which a centre may choose to
            use, are separate services with their own terms. We are not responsible for them.
          </p>
        </Panel>

        <Panel title="Changes to these terms">
          <p>
            When these terms change, we update this page and the date at the top, and tell centres
            before a significant change takes effect. Using Target after a change means accepting the
            new terms.
          </p>
        </Panel>

        <Panel title="Law and disputes">
          <p>
            These terms are governed by the law of the Republic of Uzbekistan. If something goes
            wrong, please message us first on <Telegram /> — most problems are quicker to settle
            that way. A dispute we cannot settle goes to the competent court of the Republic of
            Uzbekistan. If a court finds part of these terms unenforceable, the rest still applies.
          </p>
        </Panel>

        <h2 className="legal__part" id="privacy">
          Privacy notice
        </h2>
        <p className="legal__lede">
          What personal information Target holds, why, who can see it, where it is kept, for how
          long, and what you can ask for. It applies to everyone who uses Target: centre staff,
          candidates, and visitors to this website.
        </p>

        <Panel title="Who is responsible for your information">
          <p>
            Information about candidates and staff is put on Target by the centre they belong to,
            and the centre decides what to enter and why. Under the Law of the Republic of
            Uzbekistan “On Personal Data”, the centre is the owner of that information and Target is
            the operator that processes it for the centre, on its instructions, under a written
            agreement with the centre.
          </p>
        </Panel>

        <Panel title="What we hold">
          <Terms
            rows={[
              [
                'Candidates',
                <>
                  A name, or a code the centre uses instead (such as S-017); the number used to sign
                  in, which need not be a phone number; a candidate number; class; the teacher
                  assigned; and an email address only if the centre adds one.
                </>,
              ],
              [
                'Staff',
                <>
                  Name, sign-in number, role (administrator or teacher), and an email address if one
                  is added.
                </>,
              ],
              [
                'Passwords',
                <>Stored only as a one-way hash, which nobody — including us — can read back.</>,
              ],
              [
                'Papers sat',
                <>
                  The answers given, Writing scripts, scores and bands, teachers’ marks and comments,
                  when each paper was started and handed in, and each time the candidate left the
                  exam screen during a paper — switched to another window or tab, left full screen
                  or closed the page — with the time. A paper can end if this happens repeatedly.
                </>,
              ],
              [
                'Security records',
                <>
                  When each account last signed in, failed sign-in attempts and temporary locks, and
                  a log of staff actions in the centre’s workspace — who did what, and when — such
                  as enrolling a candidate, resetting a password or releasing results.
                </>,
              ],
              [
                'The centre',
                <>Its name, its address on Target, logo, colours, contact email and exam settings.</>,
              ],
            ]}
          />
        </Panel>

        <Panel title="What we do not collect">
          <p>
            We do not record IP addresses, which browser or device you use, or where you are. We do
            not ask for dates of birth, passport or ID numbers, photographs, audio or video of
            candidates, or payment details, and we hold no biometric or genetic data. There are no
            advertising, analytics or tracking tools on Target, and no third-party cookies.
          </p>
          <p>
            To slow down anyone guessing passwords, the server counts recent sign-in attempts from
            each connection for up to ten minutes, in memory only — nothing about the connection is
            written down. Our hosting provider’s network necessarily sees the address a request
            comes from in order to deliver it.
          </p>
        </Panel>

        <Panel title="Cookies and your browser">
          <p>
            Target sets one cookie, <code>ielts_rt</code>, which keeps you signed in. The page’s
            scripts cannot read it, it is sent only to Target’s sign-in address, and it ends after
            30 days or when you sign out. It is needed for signing in to work, so there is nothing
            to opt into.
          </p>
          <p>
            While you work, Target keeps a few things in your browser’s session storage — the
            highlights and notes you make during a paper, and where you were in the console — so
            that reloading the page does not lose them. Highlights and notes are never sent to us.
            Session storage is cleared when you close the tab.
          </p>
        </Panel>

        <Panel title="Why we use it">
          <p>
            To run Target for your centre: to sign you in, deliver and time papers, save answers as
            they are given, mark Listening and Reading, let teachers mark Writing, and show results
            to the candidate and their teachers. To keep accounts and papers secure, and to let a
            centre see what was done in its workspace and by whom. To answer questions and requests
            for help. And where the law requires it.
          </p>
          <p>
            We do not sell personal information, use it for advertising, share it with other
            centres, or use candidates’ answers or writing to train artificial-intelligence models.
          </p>
        </Panel>

        <Panel title="Consent">
          <p>
            Uzbek law generally requires a person’s consent before their personal data is
            processed. The centre asks for that consent when it enrols a candidate or takes on a
            member of staff, and tells them their information is held on Target. For a candidate
            under 18, consent must be given in writing by a parent or guardian.
          </p>
          <p>
            You can withdraw your consent at any time by telling your centre or us. Your account is
            then closed, and your information is erased as described under “Your rights”.
          </p>
        </Panel>

        <Panel title="Who can see it">
          <Terms
            rows={[
              [
                'Your centre',
                <>
                  A candidate sees their own papers and results. A teacher sees only the candidates
                  assigned to them. The centre’s administrator sees the whole centre.
                </>,
              ],
              [
                'Target',
                <>
                  Only when needed to help the centre at its request, to keep the service secure and
                  working, or where the law requires — and everyone who does is bound to keep it
                  confidential.
                </>,
              ],
              [
                'Service providers',
                <>
                  Neon Inc. hosts the database, in Frankfurt, Germany. Railway Corp. runs the
                  application and stores uploaded audio and images, in Amsterdam, the Netherlands.
                  If a centre’s staff use photo import, the photographed pages of a paper are sent
                  to Anthropic PBC, in the United States, to read their text; no information about
                  candidates is sent. Each is bound by terms that protect the data.
                </>,
              ],
              ['Authorities', <>Only where the law requires it.</>],
              [
                'A new owner',
                <>
                  If Target is sold or merged, personal information passes to the new operator only
                  on the terms of this notice, and centres are told first.
                </>,
              ],
            ]}
          />
          <p>Nobody else. We do not sell or rent personal information.</p>
        </Panel>

        <Panel title="Where it is kept">
          <p>
            On servers in Germany and the Netherlands. Uzbek law requires biometric and genetic data
            to be kept in Uzbekistan; Target holds neither. Other personal data may be kept in
            countries recognised as protecting it adequately, and Germany and the Netherlands are
            both on the list approved by the Cabinet of Ministers of the Republic of Uzbekistan.
          </p>
        </Panel>

        <Panel title="How long we keep it">
          <Terms
            rows={[
              [
                'Writing scripts',
                <>
                  Deleted automatically two days after the result is released to the candidate. The
                  marks, bands and comments are kept.
                </>,
              ],
              [
                'Sign-ins',
                <>
                  Deleted when you sign out, or when your password is changed or reset, and otherwise
                  automatically a day after they expire.
                </>,
              ],
              [
                'Answers and results',
                <>Until the centre deletes them, or until the centre stops using Target.</>,
              ],
              [
                'Accounts',
                <>
                  When a centre removes someone, the account is closed at once and can no longer
                  sign in. The record stays with the centre’s results until the centre stops using
                  Target, or until the person asks for it to be erased.
                </>,
              ],
              ['Activity log', <>For as long as the centre uses Target.</>],
              [
                'When a centre leaves',
                <>
                  Its information is deleted within 30 days of the end of its agreement, unless the
                  law requires something to be kept longer.
                </>,
              ],
              [
                'Backups',
                <>
                  Our database provider keeps a short rolling history so that the service can be
                  restored after a fault. Deleted information drops out of it as the history moves
                  on.
                </>,
              ],
            ]}
          />
        </Panel>

        <Panel title="Keeping it secure">
          <p>
            Every connection to Target is encrypted. Passwords are stored only as one-way hashes, and
            an account locks for a while after repeated wrong passwords. Teachers can reach only
            their own candidates, and staff actions are logged.
          </p>
          <p>
            No system is perfectly secure. If a breach affects personal information, we will tell
            the centres affected without undue delay, help them respond, and notify the authorised
            state body as the law requires.
          </p>
        </Panel>

        <Panel title="Your rights">
          <p>Under the Law “On Personal Data” you can:</p>
          <ul className="legal__list">
            <li>
              know whether information about you is held, what it is, why, and who it has been
              shared with;
            </li>
            <li>see it, and have it corrected if it is wrong;</li>
            <li>
              have it blocked or erased when it is no longer needed or was not lawfully obtained;
            </li>
            <li>withdraw your consent;</li>
            <li>complain to the authorised state body for personal data, or to a court.</li>
          </ul>
          <p>
            Your centre holds your record, so ask them first — they can change most of it
            themselves. You can also message us on <Telegram />; we will work with your centre and
            answer within the time the law allows. We may need to confirm who you are before acting
            on a request.
          </p>
        </Panel>

        <Panel title="Children">
          <p>
            Many candidates are under 18. A centre must have written consent from a parent or
            guardian before it enrols a candidate under 18. Target holds about them only what their
            centre enters and what they do in a paper, and shows them no advertising.
          </p>
          <p>
            A parent or guardian with a question, or who did not give consent, can contact the
            centre or us, and we will help remove the information.
          </p>
        </Panel>

        <Panel title="Changes to this notice">
          <p>
            When this notice changes, we update this page and the date at the top. If a change
            affects how centres’ information is handled, we tell centres before it takes effect.
          </p>
        </Panel>

        <h2 className="legal__part" id="contact">
          Contact
        </h2>

        <Panel title="Getting in touch">
          <p>
            For anything about Target or your information — or to bring Target to your centre —
            message us on <Telegram />. Telegram is a separate service with its own privacy policy.
          </p>
        </Panel>

        <p className="muted">
          This page is a plain statement of how Target works. It is not legal advice, and it does
          not replace a centre’s own terms with its students.
        </p>

        <p>
          <Link className="btn" to="/">
            Back to Target
          </Link>
        </p>
      </div>
    </div>
  );
}
